import React, { useEffect, useState } from "react"
import { Avatar, Box, Button, Divider, List, ListItem, ListItemAvatar, ListItemText, MenuItem, Pagination, PaginationItem, Select, TextField, Theme, Typography, useMediaQuery } from "@mui/material"
import { Dayjs } from "dayjs";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { api } from "../../config/apiCaller.config";
import { formatToDateDefault } from "../../utils/formatToDateDefault";
import noImage from '../../assets/img/no_image.jpg';
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const MyRedemptionPage: React.FC = () => {

    const [page, setPage] = useState(1);
    const [redemptionItems, setRedemptionItems] = useState([]);
    const [limit] = useState(6);
    const [totalPages, setTotalPages] = useState(0);
    const [from, setFrom] = useState<string | undefined>();
    const [to, setTo] = useState<string | undefined>();
    const [status, setStatus] = useState<string | undefined>();

    const [fromInput, setFromInput] = useState<Dayjs | null>();
    const [toInput, setToInput] = useState<Dayjs | null>();
    const [statusInput, setStatusInput] = useState<string>('');

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

    useEffect(() => {
        void loadRedemptions();
    } , [page, limit, from, to, status]);

    const loadRedemptions = async () => {
        try {
            const response = await api.get(`/redemptions/me?page=${page}&limit=${limit}${
                from ? `&from=${from}` : ''}${
                    to ? `&to=${to}` : ''}${
                        status ? `&status=${status}` : ''}`);
            
            if (response.status === 200) {
                setRedemptionItems(response.data.data);
                setTotalPages(response.data.totalPages)
            }
        } catch (error: any) {
            setRedemptionItems([]);
            setTotalPages(0);
        }
        
    }

    const clearFilters = async () => {
        setPage(1);

        setFromInput(undefined);
        setToInput(undefined);
        setStatusInput('');
    }

    const applyFilters = async () => {
        setPage(1);

        const from = fromInput ? formatToDateDefault(fromInput) : undefined;
        const to = toInput ? formatToDateDefault(toInput) : undefined;
        const status = statusInput ?? undefined;

        setFrom(from);
        setTo(to);
        setStatus(status);
    }

    const formatStatus = (status: string) => {
        switch (status) {
            case 'pending':
                return 'Pendente';
            case 'completed':
                return 'Concluido';
            case 'cancelled':
                return 'Cancelado';
            default:
                return status;
        }
    }

    const checkStatusColor = (status: string) => {
        switch (status) {
            case 'pending':
                return 'orange';
            case 'completed':
                return 'green';
            case 'cancelled':
                return 'red';
            default:
                return 'black';
        }
    }


    return (
        <Box height="100vh" bgcolor="#FFFFFF">
            <Typography variant='h4' color='textPrimary'>Meus resgates</Typography>
            <Typography variant="subtitle1" color="textSecondary">Aqui você pode acompanhar seus resgates e seus status</Typography>

            <Box display="flex" justifyContent="center" p={2}>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        applyFilters();
                    }}
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px",
                        justifyContent: "center"
                    }}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                        <Box display="flex" gap={2} flexWrap={isMobile ? "nowrap" : "wrap"}>
                            <DatePicker
                                label="De"
                                disableFuture
                                format="DD/MM/YYYY"
                                value={fromInput}
                                onChange={(newValue) => setFromInput(newValue)}
                            />
                            <DatePicker
                                label="Até"
                                disableFuture
                                format="DD/MM/YYYY"
                                value={toInput}
                                onChange={(newValue) => setToInput(newValue)}
                            />
                        </Box>
                    </LocalizationProvider>

                    <Select displayEmpty value={statusInput} onChange={(e) => setStatusInput(e.target.value as string)} style={{ minWidth: 120 }}>
                        <MenuItem value="" disabled>Status</MenuItem>
                        <MenuItem value="pending">Pendente</MenuItem>
                        <MenuItem value="completed">Concluido</MenuItem>
                        <MenuItem value="cancelled">Cancelado</MenuItem>
                    </Select>

                    <Box display="flex" gap={1}>
                        <Button type="button" variant="contained" style={{ backgroundColor: "#D89274" }} onClick={clearFilters}>
                            Limpar filtros
                        </Button>
                        <Button type="submit" variant="contained" style={{ backgroundColor: "#D89274" }}>
                            Filtrar
                        </Button>
                    </Box>
                </form>
            </Box>

            <Box display={'flex'} justifyContent={'center'} p={2}>
                <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                    {redemptionItems?.length? redemptionItems.map((item: any, index) =>
                        <React.Fragment key={item._id}>
                            <ListItem alignItems="flex-start" style={{ paddingLeft: 0 }}>
                                <ListItemAvatar>
                                    {item.items[0]?.medias?.length ? 
                                        <Avatar  
                                            alt={item.items[0].name}
                                            src={`http://${process.env.REACT_APP_API_URL}/rewards/image/${item.items[0].medias[0]}`}
                                        />
                                        :
                                        <Avatar 
                                            alt="Item Image" 
                                            src={noImage}
                                        />
                                    }
                                </ListItemAvatar>
                                <ListItemText
                                    primary={item.items[0]?.name}
                                    secondary={
                                        <React.Fragment>
                                        <Typography
                                            component="span"
                                            variant="body2"
                                            sx={{ color: 'text.primary', display: 'inline' }}
                                        >
                                            <b>Data do resgate</b>: {new Date(item.redeemedAt).toLocaleDateString() }
                                        </Typography>
                                        <b> Pontos: </b> {item.points}
                                        </React.Fragment>
                                    }
                                />
                                <ListItemText style={{ color: checkStatusColor(item.status) }} primary={formatStatus(item.status)} />
                                </ListItem>
                            <Divider variant="inset" component="li" />
                        </React.Fragment>)
                        : 
                        <Typography variant="h6" color="textSecondary" textAlign={'center'}>Nenhum resgate encontrado</Typography>
                    }
                </List>
            </Box>

            <Box display={'flex'} justifyContent={'center'} p={2}>
                <Pagination
                    count={totalPages}
                    page={page}
                    renderItem={(item) => (
                        <PaginationItem
                            slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                            {...item}
                            onClick={() => {setPage(item.page ?? 1)}}
                        />
                    )}
                />
            </Box>

        </Box>
    )
}

export { MyRedemptionPage }